import { useEffect } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import SiteFooter from "../components/SiteFooter";
import "./professional.css";

const ProjectResourceLinks = ({ label, resources }) => (
  <div className="ProjectChapterResources" aria-label={label}>
    {resources.map((resource) => (
      <a href={resource.href} target="_blank" rel="noreferrer" key={resource.href}>
        {resource.label} <FiArrowUpRight aria-hidden="true" />
      </a>
    ))}
  </div>
);

const ProjectChapter = ({
  title,
  image,
  imageAlt,
  imageHref,
  imageIsLogo = false,
  children,
  resources = [],
  resourceGroups = [],
}) => (
  <article className="ProjectChapter">
    <div className="ProjectChapterBody">
      <a
        className={`ProjectChapterVisual${imageIsLogo ? " ProjectChapterVisualLogo" : ""}`}
        href={imageHref}
        target="_blank"
        rel="noreferrer"
        aria-label={`View source for ${title}`}
      >
        <img src={image} alt={imageAlt} loading="lazy" />
      </a>
      <div className="ProjectChapterCopy">
        <h3>{title}</h3>
        <p>{children}</p>
        {resources.length > 0 && (
          <ProjectResourceLinks label={`${title} resources`} resources={resources} />
        )}
        {resourceGroups.length > 0 && (
          <div className="ProjectChapterResourceGroups">
            {resourceGroups.map((group) => (
              <section className="ProjectChapterResourceGroup" key={group.label}>
                <h4>{group.label}</h4>
                <ProjectResourceLinks
                  label={`${title}: ${group.label}`}
                  resources={group.resources}
                />
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  </article>
);

function Projects() {
  useEffect(() => {
    document.title = "Projects | Jérôme Lecoq";
  }, []);

  return (
    <main className="ProfessionalPage ProjectsPage">
      <section className="ProjectsHero">
        <h1>Projects</h1>
        <p>
          My work spans several complementary areas, organized below: open observatories
          and datasets, neural computation, imaging instruments, AI for science, open
          knowledge, and brain metabolism. Together, they connect questions about how the
          brain works with the tools, analyses, and shared infrastructure needed to answer
          them.
        </p>
      </section>

      <section className="ProjectsList" aria-label="Projects">
        <article id="openscope" className="ProjectEntry">
          <div className="ProjectBody">
            <p className="ProjectStatus">Active</p>
            <h2>OpenScope</h2>
            <p className="ProjectLead">
              A shared brain observatory where researchers propose experiments and a
              dedicated team runs them on standardized, high-throughput platforms.
            </p>
            <p>
              I joined the Allen Institute in 2015 to help establish the first brain observatory:
              a standardized platform connecting high-throughput two-photon imaging,
              experimental design, quality control, and open data production. I co-established
              OpenScope, secured its funding, and lead the program, opening that
              infrastructure to questions proposed by researchers around the world. It
              brings instruments, data systems, analysis, and open science together in one
              shared platform.
            </p>

            <figure className="ProjectIntroVideo">
              <div className="ProjectIntroVideoFrame">
                <iframe
                  src="https://www.youtube-nocookie.com/embed/13r9n0pSDLc"
                  title="The World's Observatory of the Mind | OpenScope"
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <figcaption>
                <a
                  href="https://www.youtube.com/watch?v=13r9n0pSDLc"
                  target="_blank"
                  rel="noreferrer"
                >
                  The World&apos;s Observatory of the Mind · Allen Institute
                  <FiArrowUpRight aria-hidden="true" />
                </a>
              </figcaption>
            </figure>

            <div className="ProjectChapters">
              <ProjectChapter
                title="Allen Brain Observatory"
                image="/project-media/brain-observatory.jpg"
                imageAlt="Allen Brain Observatory workflow from surgery through imaging and histology"
                imageHref="https://doi.org/10.1038/s41593-019-0550-9"
                resources={[
                  {
                    href: "https://doi.org/10.1038/s41593-019-0550-9",
                    label: "Brain Observatory paper",
                  },
                  {
                    href: "https://doi.org/10.1038/s41593-025-01904-7",
                    label: "Multimodal cortical circuits",
                  },
                  {
                    href: "https://doi.org/10.1038/s41586-020-03171-x",
                    label: "Visual-system spiking hierarchy",
                  },
                  {
                    href: "https://doi.org/10.1167/jov.21.9.2901",
                    label: "Standardized receptive-field survey",
                  },
                  {
                    href: "https://doi.org/10.1038/s41586-019-1716-z",
                    label: "Cortical and thalamic connectivity",
                  },
                  {
                    href: "https://doi.org/10.1371/journal.pone.0213924",
                    label: "Visual-area variation",
                  },
                  {
                    href: "https://doi.org/10.1016/j.cell.2018.06.035",
                    label: "Transgenic mouse lines",
                  },
                  {
                    href: "https://doi.org/10.1371/journal.pcbi.1006535",
                    label: "Visual cortical circuit model",
                  },
                  {
                    href: "https://doi.org/10.1101/2023.02.14.528085",
                    label: "Stimulus novelty · Preprint",
                  },
                  {
                    href: "https://doi.org/10.1101/826701",
                    label: "Direction-reversing neurons · Preprint",
                  },
                  {
                    href: "https://doi.org/10.1101/2025.04.24.649900",
                    label: "Hebbian assemblies · Preprint",
                  },
                ]}
              >
                Standardized large-scale experiments, quality control, and data
                production made measurements comparable across animals and studies.
              </ProjectChapter>
              <ProjectChapter
                title="OpenScope"
                image="/project-media/openscope-logo.png"
                imageAlt="OpenScope logo"
                imageHref="https://www.allenneuraldynamics.org/projects/openscope"
                imageIsLogo
                resources={[
                  {
                    href: "https://www.allenneuraldynamics.org/projects/openscope",
                    label: "OpenScope at Allen",
                  },
                  {
                    href: "https://doi.org/10.1016/j.neuron.2022.09.033",
                    label: "Next-generation brain observatories",
                  },
                  {
                    href: "https://doi.org/10.1523/ENEURO.0280-21.2021",
                    label: "OpenScope differentiation study",
                  },
                  {
                    href: "https://doi.org/10.1523/ENEURO.0491-21.2022",
                    label: "Cortical broadcast study",
                  },
                  {
                    href: "https://doi.org/10.1523/JNEUROSCI.1009-23.2023",
                    label: "Pattern violations across days",
                  },
                  {
                    href: "https://doi.org/10.1038/s41593-025-02055-5",
                    label: "Recurrent pattern completion",
                  },
                  {
                    href: "https://doi.org/10.48550/arXiv.2504.09614",
                    label: "Predictive-processing community preprint",
                  },
                  {
                    href: "https://doi.org/10.1101/2024.10.02.616378",
                    label: "Hierarchical prediction · Preprint",
                  },
                  {
                    href: "https://doi.org/10.1101/2023.06.02.543483",
                    label: "Temporal context and drift · Preprint",
                  },
                  {
                    href: "https://2024.ccneuro.org/pdf/275_Paper_authored_CCN-2024-PassiveGLO-(Submission-Copy).pdf",
                    label: "Oddball current-source density · CCN 2024",
                  },
                ]}
              >
                A brain observatory that invites researchers to propose experiments and
                gives selected questions access to a dedicated experimental team.
              </ProjectChapter>
              <ProjectChapter
                title="Reusable datasets"
                image="/project-media/openscope-databook.png"
                imageAlt="OpenScope Databook website with reproducible neuroscience notebooks"
                imageHref="https://alleninstitute.github.io/openscope_databook/"
                resourceGroups={[
                  {
                    label: "Explore",
                    resources: [
                      {
                        href: "https://openscopedatafront.web.app/about",
                        label: "OpenScope Data Portal",
                      },
                      {
                        href: "https://dandiarchive.org/",
                        label: "DANDI Archive",
                      },
                      {
                        href: "https://alleninstitute.github.io/openscope_databook/",
                        label: "OpenScope Databook",
                      },
                      {
                        href: "https://doi.org/10.1038/s41597-023-02214-y",
                        label: "Longitudinal data paper",
                      },
                    ],
                  },
                  {
                    label: "Versioned DANDI releases",
                    resources: [
                      {
                        href: "https://doi.org/10.48324/dandi.000036/0.230515.1917",
                        label: "000036 · Neurophysiological differentiation",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.000037/0.240209.1623",
                        label: "000037 · Somata and apical dendrites",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.000248/0.240502.2344",
                        label: "000248 · Illusion",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.000253/0.240923.1441",
                        label: "000253 · Global/local oddball · Mouse",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.000336/0.260325.1802",
                        label: "000336 · Dendritic coupling",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.000488/0.230602.2022",
                        label: "000488 · Temporal context and expectation",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.000535/0.230524.0416",
                        label: "000535 · Periodic visual stimulation",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.000563/0.250311.2145",
                        label: "000563 · Temporal barcode",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.000617/0.250312.0130",
                        label: "000617 · Sequence learning",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.000690/0.260330.2144",
                        label: "000690 · Vision to hippocampus",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.001191/0.260624.2038",
                        label: "001191 · Loop",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.001417/0.260626.2154",
                        label: "001417 · Psycode",
                      },
                      {
                        href: "https://doi.org/10.48324/dandi.001838/0.260617.2033",
                        label: "001838 · Global/local oddball · Macaque",
                      },
                    ],
                  },
                  {
                    label: "Community datasets · Public drafts",
                    resources: [
                      {
                        href: "https://dandiarchive.org/dandiset/001424/draft",
                        label: "001424 · SLAP2 · Draft",
                      },
                      {
                        href: "https://dandiarchive.org/dandiset/001637/draft",
                        label: "001637 · Electrophysiology · Draft",
                      },
                      {
                        href: "https://dandiarchive.org/dandiset/001768/draft",
                        label: "001768 · Mesoscope · Draft",
                      },
                    ],
                  },
                ]}
              >
                Versioned project releases and clearly marked community drafts make
                standardized neurophysiology data, metadata, and analysis-ready outputs
                available well beyond the originating experiments.
              </ProjectChapter>
            </div>
          </div>
        </article>

        <article id="understanding-the-brain" className="ProjectEntry">
          <div className="ProjectBody">
            <p className="ProjectStatus">Active</p>
            <h2>Understanding the brain</h2>
            <p className="ProjectLead">
              Studying how populations of neurons transform sensory input and prior
              experience into perception, memory, and action.
            </p>
            <p>
              Instruments and observatories are means to a scientific question: how does
              the brain compute? Across visual cortex, amygdala, cerebellum, and the
              olfactory system, my work and collaborations have examined the fidelity and
              reliability of sensory codes, communication between cortical areas,
              responses to unexpected events, recurrent pattern completion during sensory
              inference, long-term associative memory, and circuit state changes during
              learned movement.
            </p>

            <div className="ProjectChapters">
              <ProjectChapter
                title="Perception and prediction"
                image="/project-media/brain-inference.jpg"
                imageAlt="Experimental design for testing recurrent pattern completion during visual inference"
                imageHref="https://doi.org/10.1038/s41593-025-02055-5"
                resources={[
                  {
                    href: "https://doi.org/10.1038/s41593-025-02055-5",
                    label: "Sensory inference",
                  },
                  {
                    href: "https://doi.org/10.1016/j.isci.2024.111728",
                    label: "Unexpected events",
                  },
                  {
                    href: "https://doi.org/10.1523/JNEUROSCI.1009-23.2023",
                    label: "Pattern violations",
                  },
                  {
                    href: "https://doi.org/10.7554/eLife.55130",
                    label: "VIP interneurons",
                  },
                  {
                    href: "https://doi.org/10.1101/2023.02.14.528085",
                    label: "Stimulus novelty · Preprint",
                  },
                  {
                    href: "https://doi.org/10.1101/2024.10.02.616378",
                    label: "Hierarchical prediction · Preprint",
                  },
                  {
                    href: "https://doi.org/10.1101/2023.06.02.543483",
                    label: "Temporal context and drift · Preprint",
                  },
                  {
                    href: "https://2024.ccneuro.org/pdf/275_Paper_authored_CCN-2024-PassiveGLO-(Submission-Copy).pdf",
                    label: "Oddball current-source density · CCN 2024",
                  },
                ]}
              >
                How cortical circuits combine incoming stimuli with learned structure,
                and how somata, dendrites, excitatory neurons, and interneurons
                respond when visual patterns violate expectation.
              </ProjectChapter>
              <ProjectChapter
                title="Population codes and circuit structure"
                image="/project-media/rumyantsev-coding.png"
                imageAlt="Rumyantsev paper recordings of large neuronal populations in visual cortex"
                imageHref="https://doi.org/10.1038/s41586-020-2130-2"
                resources={[
                  {
                    href: "https://doi.org/10.1038/s41586-020-2130-2",
                    label: "Rumyantsev et al. · Coding fidelity",
                  },
                  {
                    href: "https://doi.org/10.1523/JNEUROSCI.6187-08.2009",
                    label: "Olfactory adaptation",
                  },
                  {
                    href: "https://doi.org/10.1371/journal.pcbi.1006535",
                    label: "Visual cortical circuit model",
                  },
                  {
                    href: "https://doi.org/10.1101/826701",
                    label: "Direction-reversing neurons · Preprint",
                  },
                  {
                    href: "https://doi.org/10.1101/2025.04.24.649900",
                    label: "Hebbian assemblies · Preprint",
                  },
                ]}
              >
                What limits the fidelity of cortical sensory representations, how
                reliable codes emerge in large populations, and how variability is shared
                between connected brain areas and constrained by circuit structure.
              </ProjectChapter>
              <ProjectChapter
                title="Learning, memory, and communication"
                image="/project-media/brain-coding.jpg"
                imageAlt="Ebrahimi paper comparing reliable population activity across connected cortical areas"
                imageHref="https://doi.org/10.1038/s41586-022-04724-y"
                resources={[
                  {
                    href: "https://doi.org/10.1038/s41586-022-04724-y",
                    label: "Ebrahimi et al. · Inter-area reliability",
                  },
                  {
                    href: "https://doi.org/10.1038/nature21682",
                    label: "Long-term memory",
                  },
                  {
                    href: "https://doi.org/10.1016/j.cell.2021.06.001",
                    label: "Skilled movement",
                  },
                ]}
              >
                How reliable activity is communicated between cortical areas, how neural
                ensembles change across long-term associative learning, and how coherent
                circuit dynamics emerge as skilled movements are learned.
              </ProjectChapter>
            </div>
          </div>
        </article>

        <article id="neuron-tools" className="ProjectEntry">
          <div className="ProjectBody">
            <p className="ProjectStatus">Previous</p>
            <h2>Tools for neurons</h2>
            <p className="ProjectLead">
              Building optical instruments, experimental methods, and processing systems
              that make previously inaccessible neuronal activity measurable and usable.
            </p>
            <p>
              From optical measurements during my Ph.D. to large-scale recording systems at
              Stanford and commercial analysis tools at Inscopix, I have worked across the
              path from instrument design to usable neural data.
            </p>

            <div className="ProjectChapters">
              <ProjectChapter
                title="Dual-axis two-photon microscope"
                image="/project-media/dual-axis.jpg"
                imageAlt="Dual-axis two-photon microscope and simultaneous cortical recording geometry"
                imageHref="https://doi.org/10.1038/nn.3867"
                resources={[
                  {
                    href: "https://doi.org/10.1038/nn.3867",
                    label: "Dual-axis imaging paper",
                  },
                  {
                    href: "https://doi.org/10.1038/s41592-023-01808-z",
                    label: "Deep brain imaging on the move",
                  },
                  {
                    href: "https://doi.org/10.7554/eLife.69068",
                    label: "Imaging and electrophysiology",
                  },
                  {
                    href: "https://doi.org/10.7554/eLife.51675",
                    label: "Spiking and fluorescence",
                  },
                  {
                    href: "https://doi.org/10.1038/nbt.1941",
                    label: "Infrared fluorescent protein",
                  },
                ]}
              >
                I developed a microscope with two articulated imaging arms to record
                nearby or distant cortical areas simultaneously in behaving mice.
              </ProjectChapter>
              <ProjectChapter
                title="Multiplexed and scalable imaging"
                image="/project-media/rumyantsev-16-beam.png"
                imageAlt="Rumyantsev paper schematic of the sixteen-channel two-photon recording system"
                imageHref="https://doi.org/10.1038/s41586-020-2130-2"
                resources={[
                  {
                    href: "https://doi.org/10.1038/s41586-020-2130-2",
                    label: "Sixteen-channel system",
                  },
                  {
                    href: "https://doi.org/10.1016/j.celrep.2016.12.004",
                    label: "Crystal skull · Million-neuron access",
                  },
                  {
                    href: "https://doi.org/10.1523/JNEUROSCI.1527-18.2019",
                    label: "Multiphoton microscopy review",
                  },
                  {
                    href: "https://doi.org/10.1016/j.jneumeth.2020.108922",
                    label: "Standardized head fixation",
                  },
                  {
                    href: "https://doi.org/10.1523/ENEURO.0207-17.2017",
                    label: "GCaMP6 cortical activity",
                  },
                  {
                    href: "https://doi.org/10.1101/503052",
                    label: "Dual-plane remote focusing · Preprint",
                  },
                ]}
              >
                A custom sixteen-channel optical system recorded large neuronal populations;
                crystal-skull access and standardized cranial hardware expanded the accessible scale.
              </ProjectChapter>
              <ProjectChapter
                title="Neural data analysis"
                image="/project-media/inscopix-logo.png"
                imageAlt="Inscopix logo"
                imageHref="https://web.archive.org/web/20151027205033/http://inscopix.com/mosaic"
                imageIsLogo
                resources={[
                  {
                    href: "https://web.archive.org/web/20151027205033/http://inscopix.com/mosaic",
                    label: "Mosaic · archived 2015",
                  },
                  {
                    href: "https://doi.org/10.1101/2021.03.24.436279",
                    label: "EXTRACT cell extraction · Preprint",
                  },
                  {
                    href: "https://doi.org/10.1038/s41592-021-01285-2",
                    label: "DeepInterpolation · Nature Methods",
                  },
                  {
                    href: "https://doi.org/10.1016/j.crmeth.2023.100505",
                    label: "AI for voltage imaging · Commentary",
                  },
                  {
                    href: "https://github.com/AllenInstitute/deepinterpolation",
                    label: "DeepInterpolation code",
                  },
                ]}
              >
                From miniscope workflows commercialized in Mosaic to statistically robust
                cell extraction and self-supervised denoising with DeepInterpolation, this
                work turns noisy imaging, electrophysiology, and fMRI recordings into
                usable neural signals.
              </ProjectChapter>
            </div>
          </div>
        </article>

        <article id="ai-science" className="ProjectEntry ProjectEntryAi">
          <div className="ProjectBody">
            <p className="ProjectStatus">Active</p>
            <h2>AI for science</h2>
            <p className="ProjectLead">
              Using AI to recover signals from noisy measurements, explore scientific
              literature at scale, and build reviews that remain traceable to their evidence.
            </p>
            <p>
              I was introduced to artificial intelligence early in my career through a
              formative experience at Sony Computer Science Laboratories in Paris. Since
              then, I have used AI and machine learning across the scientific process:
              recovering physiological signals from noisy recordings, exploring large
              bodies of literature, and building review workflows in which evidence,
              criticism, and citation checks remain visible.
            </p>

            <div className="ProjectChapters">
              <ProjectChapter
                title="Self-supervised denoising"
                image="/project-media/deepinterpolation.jpg"
                imageAlt="DeepInterpolation architecture and denoising results across neural recording modalities"
                imageHref="https://doi.org/10.1038/s41592-021-01285-2"
                resources={[
                  {
                    href: "https://doi.org/10.1038/s41592-021-01285-2",
                    label: "DeepInterpolation · Nature Methods",
                  },
                  {
                    href: "https://doi.org/10.1016/j.crmeth.2023.100505",
                    label: "AI for voltage imaging · Commentary",
                  },
                  {
                    href: "https://github.com/AllenInstitute/deepinterpolation",
                    label: "DeepInterpolation code",
                  },
                ]}
              >
                I developed DeepInterpolation to learn the spatiotemporal structure of raw
                recordings and remove independent noise without clean ground-truth targets.
                Across calcium imaging, extracellular electrophysiology, and fMRI, it
                improved signal-to-noise without sacrificing spatial or temporal resolution,
                revealing physiological signals obscured in the raw data.
              </ProjectChapter>
              <ProjectChapter
                title="Literature maps"
                image="/project-media/literature-maps.png"
                imageAlt="Interactive embedding map of thousands of mouse visual-cortex publications"
                imageHref="https://github.com/AllenInstitute/openai_tools"
                resources={[
                  {
                    href: "https://github.com/AllenInstitute/openai_tools",
                    label: "Literature tools",
                  },
                  {
                    href: "https://x.com/LecoqJerome/status/1647448796346093569",
                    label: "Original X thread",
                  },
                ]}
              >
                I created <code>openai_tools</code> as an experimental toolkit for summarizing
                publication PDFs and building local, cached collections of PubMed records
                and paper embeddings. Its interactive maps support exploration from a folder
                of papers to searches spanning thousands of publications.
              </ProjectChapter>
              <ProjectChapter
                title="Auditable review workflows"
                image="/project-media/review-workflow.png"
                imageAlt="Multi-agent computational review pipeline with separated production and validation phases"
                imageHref="https://github.com/AllenNeuralDynamics/ComputationalReviewTemplate"
                resources={[
                  {
                    href: "https://github.com/AllenNeuralDynamics/ComputationalReviewTemplate",
                    label: "Review workflow",
                  },
                  {
                    href: "https://doi.org/10.5281/zenodo.21213212",
                    label: "Citable release",
                  },
                  {
                    href: "https://www.anthropic.com/news/claude-science-ai-workbench",
                    label: "Anthropic highlight",
                  },
                ]}
              >
                With collaborators, I am developing staged
                multi-agent workflows for automating long-form scientific reviews. Evidence gathering, writing, figure construction,
                blinded criticism, and full-text citation verification are automated using AI agents. Each phase leaves
                structured evidence and provenance artifacts so the final narrative can be
                audited against its sources.
              </ProjectChapter>
            </div>
          </div>
        </article>

        <article id="flow-of-ideas" className="ProjectEntry ProjectEntryPersonal">
          <div className="ProjectBody">
            <p className="ProjectStatus">Active</p>
            <h2>Flow of ideas</h2>
            <p className="ProjectLead">
              Experiments in helping ideas move openly while preserving the identity,
              credit, and evidence that make them useful.
            </p>
            <p>
              This work sits alongside the main scientific thread and explores three
              connected barriers: who can publish an idea, how collaborative work is
              credited, and how the evidence beneath a claim can remain inspectable and
              reusable.
            </p>

            <div className="ProjectChapters">
              <ProjectChapter
                title="SmallChains"
                image="/project-media/smallchains.png"
                imageAlt="SmallChains blog showing wallet-authenticated posts and replies"
                imageHref="/smallchains"
                resources={[
                  {
                    href: "/smallchains",
                    label: "How SmallChains works",
                  },
                  {
                    href: "/blog",
                    label: "Read the blog",
                  },
                ]}
              >
                A wallet-signed publishing experiment on Polygon. Each post is minted as
                a transferable ERC-721; replies, quotes, edits, and direct tips become
                inspectable links and events.
              </ProjectChapter>
              <ProjectChapter
                title="AuthorshipExtractor"
                image="/project-media/authorship-interface.png"
                imageAlt="AuthorshipExtractor paper with its interactive collaboration explorer"
                imageHref="https://allenneuraldynamics.github.io/AuthorshipExtractor/"
                resources={[
                  {
                    href: "https://allenneuraldynamics.github.io/AuthorshipExtractor/",
                    label: "Interactive paper",
                  },
                  {
                    href: "https://github.com/AllenNeuralDynamics/AuthorshipExtractor",
                    label: "AuthorshipExtractor code",
                  },
                ]}
              >
                AuthorshipExtractor replaces the compressed, ordered author list with an
                interactive view of CRediT roles and effort, section and figure
                attribution, collaboration networks, and project timelines.
              </ProjectChapter>
              <ProjectChapter
                title="Decoupled evidence layer"
                image="/project-media/evidence-layer.svg"
                imageAlt="Diagram of a citable evidence record connecting data, software, protocols, figures, and contributors"
                imageHref="https://allenneuraldynamics.github.io/decoupled-evidence-layer/"
                resources={[
                  {
                    href: "https://allenneuraldynamics.github.io/decoupled-evidence-layer/",
                    label: "Consensus paper",
                  },
                  {
                    href: "https://github.com/AllenNeuralDynamics/decoupled-evidence-layer",
                    label: "Evidence-layer source",
                  },
                ]}
              >
                The decoupled evidence layer proposes interoperable, independently citable
                evidence records for AI-era science, allowing evidence to be shared and
                examined beyond the boundaries of a single paper.
              </ProjectChapter>
            </div>
          </div>
        </article>

        <article id="brain-metabolism" className="ProjectEntry ProjectEntryMetabolism">
          <div className="ProjectBody">
            <p className="ProjectStatus">Previous</p>
            <h2>Brain metabolism</h2>
            <p className="ProjectLead">
              Measuring how neuronal activity, local blood flow, and oxygen use are coupled
              in the living brain.
            </p>
            <p>
              My Ph.D. with Serge Charpak examined how neural activity drives oxygen
              consumption and local blood flow in the living olfactory bulb. We found that
              odor-evoked oxygen use was concentrated in dendritic and synaptic processing
              rather than axonal spike propagation, identified postsynaptic glutamate
              signaling and dendritic calcium as key links to functional hyperemia, and
              showed that blood-flow responses can sometimes misrepresent local neural
              activity. To observe oxygen delivery directly, I developed two-photon
              phosphorescence lifetime imaging for simultaneous measurements of oxygen and
              blood flow in individual deep vessels, revealing red-blood-cell-associated
              oxygen transients, diffusion-based arteriovenous shunts, and stimulus-evoked
              oxygen dynamics.
            </p>

            <div className="ProjectChapters">
              <ProjectChapter
                title="Neuronal oxygen use during odor processing"
                image="/project-media/oxygen-fig3.jpg"
                imageAlt="Oxygen sensor, local field potential, capillary flow, and measured odor-evoked oxygen transients"
                imageHref="https://doi.org/10.1523/JNEUROSCI.4817-08.2009"
                resources={[
                  {
                    href: "https://doi.org/10.1523/JNEUROSCI.4817-08.2009",
                    label: "JNeurosci oxygen-consumption paper",
                  },
                ]}
              >
                Odor stimulation revealed that oxygen use in synapse-rich glomeruli arose
                primarily from dendritic activation, whereas action-potential propagation
                in olfactory axons produced no local decrease in tissue oxygen.
              </ProjectChapter>
              <ProjectChapter
                title="Neuronal activity and blood flow"
                image="/project-media/coupling-fig3.jpg"
                imageAlt="Odor-evoked neuronal potential and red-blood-cell velocity responses"
                imageHref="https://doi.org/10.1523/JNEUROSCI.3141-06.2007"
                resources={[
                  {
                    href: "https://doi.org/10.1523/JNEUROSCI.3141-06.2007",
                    label: "Blood flow and neuronal activity",
                  },
                  {
                    href: "https://doi.org/10.1523/JNEUROSCI.3146-10.2011",
                    label: "Local functional hyperemia",
                  },
                ]}
              >
                Postsynaptic glutamate-receptor activation and dendritic calcium linked
                odor-evoked neuronal activity to local blood flow. Later experiments showed
                that functional hyperemia can sometimes be an unreliable marker of local
                neural activation.
              </ProjectChapter>
              <ProjectChapter
                title="Vessel-scale oxygen and blood flow"
                image="/project-media/oxygen-flow.jpg"
                imageAlt="Two-photon measurements of oxygen and blood flow in deep cerebral vessels"
                imageHref="https://doi.org/10.1038/nm.2394"
                resources={[
                  {
                    href: "https://doi.org/10.1038/nm.2394",
                    label: "Nature Medicine measurement paper",
                  },
                  {
                    href: "https://doi.org/10.1007/978-1-59745-543-5_4",
                    label: "Capillary-flow imaging method",
                  },
                  {
                    href: "https://doi.org/10.1117/1.NPh.11.1.010401",
                    label: "Conversation with Serge Charpak",
                  },
                  {
                    href: "https://doi.org/10.1073/pnas.0902599107",
                    label: "Pituitary microcirculation collaboration",
                  },
                ]}
              >
                Simultaneous two-photon measurements of oxygen and blood flow in individual
                deep vessels revealed red-blood-cell-associated oxygen transients,
                diffusion-based arteriovenous shunts, and stimulus-evoked oxygen dynamics
                in capillaries and surrounding neural tissue.
              </ProjectChapter>
            </div>
          </div>
        </article>
      </section>

      <SiteFooter />
    </main>
  );
}

export default Projects;